import { ref } from 'vue'

import { defineStore } from 'pinia'

import { CREATE_SCHEDULER } from '@/modules/module-factory-dashboard/queries/createScheduler'
import { CREATE_WATCHER } from '@/modules/module-factory-dashboard/queries/createWatcher'
import { DELETE_SCHEDULER } from '@/modules/module-factory-dashboard/queries/deleteScheduler'
import { DELETE_WATCHER } from '@/modules/module-factory-dashboard/queries/deleteWatcher'
import { GET_ENTITY_QUERY } from '@/modules/module-factory-dashboard/queries/getEntity'
import {
  createEntityListPath,
  GET_ENTITY_LIST_QUERY,
} from '@/modules/module-factory-dashboard/queries/getEntityList'
import { GET_SCHEDULER_LIST_QUERY } from '@/modules/module-factory-dashboard/queries/getSchedulerList'
import { GET_WATCHER_LIST_QUERY } from '@/modules/module-factory-dashboard/queries/getWatcherList'
import { TEST_QUERY_SCHEDULER } from '@/modules/module-factory-dashboard/queries/testQueryScheduler'
import { UPDATE_SCHEDULER } from '@/modules/module-factory-dashboard/queries/updateScheduler'
import { UPDATE_WATCHER } from '@/modules/module-factory-dashboard/queries/updateWatcher'
import { useUserStore } from '@/stores/user'
import { IEntity, IScheduler, IWatcher } from '@/types/shared'
import apolloClient from '@/utils/apollo'

export const useCoreStore = defineStore('core', () => {
  // state
  const entityIds = ref<string[]>([])
  const regionId = ref('')
  const regions = ref<IEntity[]>([])
  const entities = ref<IEntity[]>([])
  const watchers = ref<IWatcher[]>([])
  const schedulers = ref<IScheduler[]>([])
  const errorWatchers = ref<IWatcher[]>([])
  const successWatchers = ref<IWatcher[]>([])

  // actions
  async function getEntity(payload: { id: string }) {
    const response = await apolloClient.query({
      query: GET_ENTITY_QUERY,
      variables: payload,
      fetchPolicy: 'no-cache',
    })
    return response.data.entity as IEntity
  }

  async function getEntityList(payload: {
    ids: string[]
    level: number | null
    type: string[] | null
    group: [] | null
  }) {
    const response = await apolloClient.query({
      query: GET_ENTITY_LIST_QUERY,
      variables: {
        ...payload,
        pathFunction: createEntityListPath,
      },
      fetchPolicy: 'no-cache',
    })
    entities.value = response.data.entity_list as IEntity[]
    return response.data.entity_list as IEntity[]
  }

  async function getRegions() {
    const userStore = useUserStore()
    const response = await apolloClient.query({
      query: GET_ENTITY_LIST_QUERY,
      variables: {
        ids: [userStore.tenant.id],
        level: null,
        type: userStore.regionTypeId,
        pathFunction: createEntityListPath,
      },
      fetchPolicy: 'no-cache',
    })

    const res = response.data.entity_list as IEntity[]
    regions.value = res

    if (regionId.value === '') {
      changeRegion(res[0].id)
    }

    if (entityIds.value.length === 0) {
      changeEntity([res[0].id])
    }

    return res
  }

  function changeEntity(ids: string[]) {
    entityIds.value = ids
  }

  function changeRegion(id: string) {
    regionId.value = id
  }

  async function getWatchers() {
    try {
      const response = await apolloClient.query({
        query: GET_WATCHER_LIST_QUERY,
        fetchPolicy: 'no-cache',
      })

      const newWatchers = response.data.watcher_list

      newWatchers.forEach((watcher: IWatcher) => {
        const origWatcher = watchers.value?.find((w: IWatcher) => w.id === watcher.id)
        if (origWatcher && origWatcher.alert !== watcher.alert) {
          errorWatchers.value.push(watcher)
        } else if (!origWatcher && watcher.alert) {
          errorWatchers.value.push(watcher)
        }
      })

      watchers.value = newWatchers
      return newWatchers
    } catch (e) {
      throw e
    }
  }

  async function getSchedulers() {
    try {
      const response = await apolloClient.query({
        query: GET_SCHEDULER_LIST_QUERY,
        fetchPolicy: 'no-cache',
      })
      schedulers.value = response.data.scheduler_list
      return response.data.scheduler_list
    } catch (e) {
      throw e
    }
  }

  async function watchWatchers() {
    const response = await apolloClient
      .watchQuery({
        query: GET_WATCHER_LIST_QUERY,
        fetchPolicy: 'no-cache',
        pollInterval: 30000,
      })
      .subscribe(({ data }) => {
        const newWatchers = data.watcher_list

        newWatchers.forEach((watcher: IWatcher) => {
          const origWatcher = watchers.value?.find((w: IWatcher) => w.id === watcher.id)
          if (origWatcher && origWatcher.alert !== watcher.alert) {
            errorWatchers.value.push(watcher)
          } else if (!origWatcher && watcher.alert) {
            errorWatchers.value.push(watcher)
          }
        })
        watchers.value = data.watcher_list
      })

    return response
  }

  async function createWatcher(watcherRequest: any) {
    const response = await apolloClient.mutate({
      mutation: CREATE_WATCHER,
      variables: { input: watcherRequest },
    })
    return response.data.create_watcher
  }

  async function updateWatcher(watcherRequest: any) {
    const response = await apolloClient.mutate({
      mutation: UPDATE_WATCHER,
      variables: { input: watcherRequest },
    })
    return response.data.update_watcher
  }

  async function deleteWatcher(payload: { id: string }) {
    const response = await apolloClient.mutate({
      mutation: DELETE_WATCHER,
      variables: { input: payload },
    })
    return response.data.delete_watcher
  }

  async function createScheduler(schedulerRequest: any) {
    const response = await apolloClient.mutate({
      mutation: CREATE_SCHEDULER,
      variables: { input: schedulerRequest },
    })
    return response.data.create_scheduler
  }

  async function updateScheduler(schedulerRequest: any) {
    const response = await apolloClient.mutate({
      mutation: UPDATE_SCHEDULER,
      variables: { input: schedulerRequest },
    })
    return response.data.update_scheduler
  }

  async function deleteScheduler(payload: { id: string }) {
    const response = await apolloClient.mutate({
      mutation: DELETE_SCHEDULER,
      variables: { input: payload },
    })
    return response.data.delete_scheduler
  }

  async function testQueryScheduler(payload: { elastic_query: JSON }) {
    const response = await apolloClient.mutate({
      mutation: TEST_QUERY_SCHEDULER,
      variables: { input: payload },
    })
    return response.data.test_query_scheduler
  }

  function install404Router() {
    // TODO: fix this
    // const l = router.resolve({ path: route404[0].path })
    // if (l.resolved.matched.length <= 0){
    //   console.log('404 installRouter')
    //   router.addRoutes(route404)
    // }
  }

  function setMapEntities(newEntities: IEntity[]) {
    entities.value = newEntities
  }

  return {
    // state
    entityIds,
    regionId,
    regions,
    entities,
    watchers,
    schedulers,
    errorWatchers,
    successWatchers,

    // actions
    getEntity,
    getEntityList,
    getRegions,
    changeEntity,
    changeRegion,
    getWatchers,
    getSchedulers,
    watchWatchers,
    createWatcher,
    updateWatcher,
    deleteWatcher,
    createScheduler,
    updateScheduler,
    deleteScheduler,
    testQueryScheduler,
    install404Router,
    setMapEntities,
  }
})
